import { safeErrorDiagnostic } from './errorDiagnostics';

const DEFAULT_THEME_COLOR = '#08111f';
const DEFAULT_BACKGROUND_COLOR = '#08111f';

type ManifestLike = Record<string, any> | string | null | undefined;

let manifestObjectUrl: string | null = null;

const resolveAppUrl = (path = '/'): string => {
  if (typeof window === 'undefined') {
    return path;
  }

  try {
    return new URL(path, window.location.origin).toString();
  } catch {
    return path;
  }
};

const appendVersionParam = (url: string, version?: string | null): string => {
  const rawUrl = String(url || '').trim();
  const rawVersion = String(version || '').trim();
  if (/^(data|blob):/i.test(rawUrl)) {
    return rawUrl;
  }
  if (!rawUrl || !rawVersion) {
    return rawUrl;
  }
  const separator = rawUrl.includes('?') ? '&' : '?';
  return `${rawUrl}${separator}v=${encodeURIComponent(rawVersion)}`;
};

const guessIconMimeType = (iconUrl: string): string => {
  if (!iconUrl) return 'image/png';
  const lower = iconUrl.toLowerCase();
  if (lower.endsWith('.ico')) return 'image/x-icon';
  if (lower.endsWith('.svg')) return 'image/svg+xml';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  return 'image/png';
};

const isInlineImageUrl = (url: string): boolean => /^data:image\//i.test(String(url || '').trim());

const manifestSafeIconUrl = (iconUrl: string): string => {
  const clean = String(iconUrl || '').trim();
  if (!clean) return resolveAppUrl('/favicon.ico');

  // Inline branding is valid manifest icon input and is especially useful for
  // tenant-specific logos returned directly by the branding API.
  if (isInlineImageUrl(clean)) {
    return clean;
  }

  // Dynamic manifests are served from a blob: URL, so relative icon paths
  // must be resolved against the application origin.
  if (/^blob:/i.test(clean)) {
    return resolveAppUrl('/favicon.ico');
  }
  return resolveAppUrl(clean);
};

const parseManifestPayload = (payload: ManifestLike): Record<string, any> | undefined => {
  if (!payload) {
    return undefined;
  }

  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch (error) {
      console.warn('Unable to parse manifest payload from branding API', safeErrorDiagnostic(error));
      return undefined;
    }
  }

  if (typeof payload === 'object') {
    return { ...payload };
  }

  return undefined;
};

export const buildManifestData = (
  manifestPayload: ManifestLike,
  brandName: string,
  iconUrl: string,
  brandingVersion?: string | null
): Record<string, any> => {
  const manifest = parseManifestPayload(manifestPayload) ?? {};
  const resolvedName = brandName || manifest.name || 'OrgCloud';
  const safeManifestIconUrl = manifestSafeIconUrl(iconUrl);
  const versionedIconUrl = appendVersionParam(safeManifestIconUrl, brandingVersion);
  manifest.name = resolvedName;
  manifest.short_name = resolvedName;
  manifest.start_url = resolveAppUrl('/?pwa=1');
  manifest.id = resolveAppUrl('/');
  manifest.scope = resolveAppUrl('/');
  manifest.display = 'standalone';
  manifest.display_override = ['standalone', 'fullscreen'];
  manifest.theme_color = DEFAULT_THEME_COLOR;
  manifest.background_color = DEFAULT_BACKGROUND_COLOR;
  if (brandingVersion) {
    manifest.orgcloud_branding_version = brandingVersion;
  }

  if (!versionedIconUrl) {
    manifest.icons = [];
  } else if (iconUrl && String(iconUrl).trim()) {
    const iconType = guessIconMimeType(safeManifestIconUrl);
    // Tenant branding wins over any generic icons supplied by a base manifest.
    manifest.icons = [
      { src: versionedIconUrl, sizes: '192x192', type: iconType, purpose: 'any' },
      { src: versionedIconUrl, sizes: '512x512', type: iconType, purpose: 'any' },
    ];
  } else if (Array.isArray(manifest.icons) && manifest.icons.length > 0) {
    manifest.icons = manifest.icons.map((icon: Record<string, any>) => {
      const rawSrc = String(icon.src || '/favicon.ico');
      const safeSrc = manifestSafeIconUrl(rawSrc);
      return {
        ...icon,
        src: appendVersionParam(safeSrc, brandingVersion),
        type: icon.type || guessIconMimeType(safeSrc),
        purpose: icon.purpose || 'any',
      };
    });
  } else {
    manifest.icons = [
      { src: resolveAppUrl('/favicon.ico'), sizes: '192x192', type: 'image/x-icon', purpose: 'any' },
      { src: resolveAppUrl('/favicon.ico'), sizes: '512x512', type: 'image/x-icon', purpose: 'any' },
    ];
  }

  return manifest;
};

const ensureLinkElement = (id: string, rel: string): HTMLLinkElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = id;
    link.rel = rel;
    document.head.appendChild(link);
  }
  return link;
};

const pickExistingManifestLink = (): HTMLLinkElement | null => {
  if (typeof document === 'undefined') return null;
  const links = Array.from(document.querySelectorAll('link[rel="manifest"]')) as HTMLLinkElement[];
  if (!links.length) return null;
  const dynamicLink = links.find((link) => link.id === 'dynamic-manifest');
  return dynamicLink || links[0];
};

export const updateManifestLink = (manifestData: Record<string, any>) => {
  if (typeof document === 'undefined') {
    return;
  }

  const manifestJson = JSON.stringify(manifestData);
  const blob = new Blob([manifestJson], { type: 'application/json' });

  if (manifestObjectUrl) {
    URL.revokeObjectURL(manifestObjectUrl);
  }

  manifestObjectUrl = URL.createObjectURL(blob);

  let manifestLink = pickExistingManifestLink();
  if (!manifestLink) {
    manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    document.head.appendChild(manifestLink);
  }
  manifestLink.id = 'dynamic-manifest';
  manifestLink.href = manifestObjectUrl;

  const otherLinks = Array.from(document.querySelectorAll('link[rel="manifest"]')) as HTMLLinkElement[];
  otherLinks.forEach((link) => {
    if (link !== manifestLink) {
      link.parentElement?.removeChild(link);
    }
  });
};

export const updateFaviconLink = (iconUrl: string, brandingVersion?: string | null) => {
  const faviconLink = ensureLinkElement('dynamic-favicon', 'icon');
  if (faviconLink) {
    const resolvedIconUrl = appendVersionParam(iconUrl, brandingVersion);
    if (resolvedIconUrl) {
      faviconLink.href = resolvedIconUrl;
    } else {
      faviconLink.removeAttribute('href');
    }
  }
};

export const ensureAppleTouchIcon = (iconUrl: string, brandingVersion?: string | null) => {
  if (typeof document === 'undefined') return;

  let appleIcon = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement | null;
  if (!appleIcon) {
    appleIcon = document.createElement('link');
    appleIcon.rel = 'apple-touch-icon';
    document.head.appendChild(appleIcon);
  }
  appleIcon.id = appleIcon.id || 'apple-touch-icon';
  const resolvedIconUrl = appendVersionParam(iconUrl, brandingVersion);
  if (resolvedIconUrl) {
    appleIcon.href = resolvedIconUrl;
  } else {
    appleIcon.removeAttribute('href');
  }
};

export const versionAssetUrl = appendVersionParam;
